import { Box, Stack, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";

import { Text, type TextProps } from "../Text";

interface ScrollingNumberCounterProps {
  startValue: number;
  endValue: number;
  duration: number;
  variant: TextProps["variant"];
  color?: string;
}

const DEFAULT_DURATION = 2000;
const MAX_FRAMES_PER_SECOND = 120;
const MIN_FRAMETIME = 1000 / MAX_FRAMES_PER_SECOND;

export function ScrollingNumberCounter(props: ScrollingNumberCounterProps) {
  const { startValue, endValue, duration = DEFAULT_DURATION, variant = "body1", color } = props;
  const theme = useTheme();
  const numberContainerRef = useRef<HTMLDivElement | null>(null);

  const isIncreasing = endValue > startValue;
  const rangeLength = Math.abs(endValue - startValue);
  const totalFrames = Math.ceil(duration / MIN_FRAMETIME);

  let numbersToScrollThrough = Array.from(
    { length: rangeLength + 1 },
    (_, index) => (isIncreasing ? startValue : endValue) + rangeLength - index
  );

  // If we are going to scroll through numbers faster than they would each be rendered,
  // reduce the number of elements displayed. This prevents us from rendering too many
  // unnecessary elements.
  if (numbersToScrollThrough.length > totalFrames) {
    const frameDivisor = Math.ceil(numbersToScrollThrough.length / totalFrames);
    numbersToScrollThrough = numbersToScrollThrough.filter(
      (_, index) =>
        index % frameDivisor === 0 || index === 0 || index === numbersToScrollThrough.length - 1
    );
  }

  const { fontSize } = theme.typography[variant];

  const animate = useCallback(() => {
    if (!numberContainerRef.current || endValue === startValue) {
      return;
    }

    const scrollToStart = `translateY(calc(-100% + ${fontSize}))`;
    const scrollToEnd = "translateY(0%)";
    // Reset position to start
    numberContainerRef.current.style.transition = "transform 0s";
    numberContainerRef.current.style.transform = isIncreasing ? scrollToStart : "none";

    // Set transition and animate
    setTimeout(() => {
      if (numberContainerRef.current) {
        numberContainerRef.current.style.transition = `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        numberContainerRef.current.style.transform = isIncreasing ? scrollToEnd : scrollToStart;
      }
    }, 50);
  }, [duration, endValue, fontSize, isIncreasing, startValue]);

  // Auto-animate on mount
  useEffect(() => {
    animate();
  }, [animate]);

  return (
    <Box
      sx={{
        width: "100%",
        height: fontSize, // show fixed height for single number
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Stack
        ref={numberContainerRef}
        direction="column"
        alignItems="center"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          height: `${numbersToScrollThrough.length * 100}%`,
        }}
      >
        {numbersToScrollThrough.map((number) => (
          <Box
            key={number}
            sx={{
              height: `${100 / numbersToScrollThrough.length}%`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text variant={variant} sx={{ color }}>
              {number}
            </Text>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
